import React, { useEffect, useState } from 'react';
import { isEqual } from 'lodash';
import { Box } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { getBookings } from '../../../services';
import { useStyles } from './styles';
import { IBooking } from '../../../types';
import { selectUpcomingEvents } from '../../../store';
import EventCard from '../../components/event-card/EventCard';
import SkeletonCard from '../../components/skeleton-card';
import usePrevious from '../../../hooks/use-previous';
import NoResults from '../../shared/no-results';
import { ReactComponent as NoBookingsSVG } from './assets/no-bookings.svg';

const useBookings = () => {
  const [loading, setLoading] = useState(true);
  const { data: myBookings } = useQuery(['my-bookings'], getBookings);
  const previousBookings = usePrevious(myBookings);
  const newBookings = !isEqual(previousBookings, myBookings);

  useEffect(() => {
    if (newBookings) {
      setTimeout(() => setLoading(false), 1500);
    }
  }, [newBookings]);

  // remove this if we add to bookings api duration
  const { upcomingEvents } = useSelector(selectUpcomingEvents);
  const bookingsWithDuration = myBookings?.map((booking) => ({
    ...booking,
    duration: upcomingEvents?.find((event) => event.id === booking.offeringId)
      ?.duration,
  }));

  return { loading, myBookings: bookingsWithDuration };
};

const BookingList = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const history = useHistory();

  const { loading, myBookings } = useBookings();

  const handleReset = () => {
    history.push('/');
  };

  return (
    <>
      {loading ? (
        <SkeletonCard />
      ) : (
        <>
          {myBookings?.length === 0 && (
            <NoResults
              containerStyle={{ marginTop: 100 }}
              illustration={<NoBookingsSVG />}
              info={t(
                'v2.bookings.section_bookedEventList_label_emptyState',
              ).replace(/\./, '.\n')}
              resetLabel={t('v2.global.button_label_bookEventNow')}
              onReset={handleReset}
            />
          )}

          <Box className={classes.bookingsContainer}>
            {myBookings?.map((booking: IBooking) => (
              <Box className={classes.bookingCard}>
                <EventCard event={booking} />
              </Box>
            ))}
          </Box>
        </>
      )}
    </>
  );
};

export default BookingList;
